import React from 'react';
import { Link } from 'react-router-dom';
import { CtaRow } from '../PublicLayout';
import LobbyMatchupCard from '../../components/games/LobbyMatchupCard';
import { ATHLETE_ATMOSPHERE } from '../../assets/atmosphere';
import {
  HOME_FEATURES,
  SAFETY_POINTS,
  FAQS,
  SPORT_TAGS,
} from '../content';

const DEMO_GAMES = [
  {
    id: 'demo-boxing',
    sport: 'BOXING',
    a: 'Harlem Eurank',
    b: 'David Papot',
    startTime: new Date(Date.now() + 5 * 24 * 60 * 60_000).toISOString(),
  },
  {
    id: 'demo-nba',
    sport: 'NBA',
    a: 'Los Angeles Lakers',
    b: 'Boston Celtics',
    startTime: new Date(Date.now() + 8 * 60 * 60_000).toISOString(),
  },
  {
    id: 'demo-soccer',
    sport: 'SOCCER',
    a: 'Spain',
    b: 'France',
    startTime: new Date(Date.now() + 36 * 60 * 60_000).toISOString(),
  },
  {
    id: 'demo-nfl',
    sport: 'NFL',
    a: 'Kansas City Chiefs',
    b: 'Buffalo Bills',
    startTime: new Date(Date.now() + 3 * 24 * 60 * 60_000).toISOString(),
  },
];

function MarketingHeroCards() {
  return (
    <div className="relative w-full" data-testid="marketing-hero-cards">
      <div
        className="absolute -inset-6 rounded-[2.5rem] blur-3xl opacity-70 pointer-events-none"
        style={{
          background:
            'radial-gradient(60% 70% at 70% 40%, rgba(140,255,74,0.18), transparent 60%), radial-gradient(50% 60% at 20% 80%, rgba(51,214,255,0.12), transparent 55%)',
        }}
        aria-hidden
      />
      <div className="relative grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        {DEMO_GAMES.slice(0, 2).map((game) => (
          <div key={game.id} className="min-w-0">
            <LobbyMatchupCard
              event={game}
              deck
              onLockIn={() => {}}
              onSendTicket={() => {}}
              className="!w-full !lg:w-full shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
            />
          </div>
        ))}
      </div>
      <p className="mt-3 text-center text-[10px] font-black uppercase tracking-widest text-[#9da7b6]">
        Real matchup cards — Lock It In or Send Ticket
      </p>
    </div>
  );
}

function FeatureCard({ icon, title, body }) {
  return (
    <article className="relative overflow-hidden rounded-2xl bg-white/[0.03] p-6 hover:bg-white/[0.05] transition-colors">
      <span className="text-[11px] font-black uppercase tracking-[0.28em] text-[#8cff4a] mb-4 block">
        {icon}
      </span>
      <h3 className="text-lg font-black tracking-tight text-white mb-2">{title}</h3>
      <p className="text-sm text-[#9da7b6] leading-relaxed">{body}</p>
    </article>
  );
}

export default function HomePage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0" aria-hidden>
          <img
            src={ATHLETE_ATMOSPHERE.heroAllSports}
            alt=""
            draggable={false}
            className="absolute right-0 top-0 h-full w-[60%] object-cover object-right opacity-35 grayscale contrast-125 hidden md:block"
            style={{
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 30%)',
              maskImage: 'linear-gradient(90deg, transparent 0%, black 30%)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#07090d] via-[#07090d]/92 to-[#07090d]/55" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-16 lg:pt-20 lg:pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8cff4a]/10 border border-[#8cff4a]/25 text-[#8cff4a] text-[10px] font-black uppercase tracking-[0.2em] mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8cff4a] animate-pulse" />
                Peer-to-peer sports challenges
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6 text-white">
                Every Game. Every Play.{' '}
                <span className="text-[#8cff4a]">Every Lock.</span>
              </h1>
              <p className="max-w-xl mx-auto lg:mx-0 text-[#9da7b6] text-base sm:text-lg font-medium leading-relaxed mb-8">
                Pick a matchup, set an entry, and go head-to-head with a friend.
                Accepted entries move to locked funds — the result decides who claims the pool.
              </p>
              <CtaRow className="lg:justify-start mb-5" />
              <p className="text-sm text-[#687386]">
                Returning member?{' '}
                <Link to="/signup?mode=login" className="text-[#8cff4a] font-bold hover:underline">Log in</Link>
              </p>
            </div>
            <div className="hidden sm:block">
              <MarketingHeroCards />
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming matchups */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20" data-testid="marketing-matchup-gallery">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8cff4a] mb-3">
            Upcoming games
          </p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white leading-tight">
            Browse upcoming games. Send a challenge or a ticket.
          </h2>
          <p className="mt-4 text-[#9da7b6] text-base leading-relaxed">
            Explore real matchup cards across sports — then Lock It In with a friend
            already on LockedIn, or Send Ticket to invite someone new.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {DEMO_GAMES.map((game) => (
            <div key={game.id} className="min-w-0">
              <LobbyMatchupCard
                event={game}
                deck
                onLockIn={() => {}}
                onSendTicket={() => {}}
                className="!w-full !lg:w-full"
              />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap justify-center gap-2 mt-8">
          {SPORT_TAGS.map((s) => (
            <span
              key={s}
              className="px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[#687386]"
            >
              {s}
            </span>
          ))}
        </div>
      </section>

      {/* What is LockedIn */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 lg:py-20 text-center">
        <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#8cff4a] mb-3">
          What is LockedIn?
        </p>
        <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-white mb-5 leading-tight">
          Head-to-head competition with friends — built around real games.
        </h2>
        <p className="text-[#9da7b6] text-base sm:text-lg font-medium leading-relaxed max-w-2xl mx-auto">
          LockedIn is a peer-to-peer challenge platform. There is no house and no odds board —
          just you, an opponent, and a matchup you both care about. Set an entry, lock it in,
          and let the final result settle the challenge.
        </p>
      </section>

      {/* Product features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8cff4a] mb-3">
            Inside the app
          </p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
            Everything you need to compete
          </h2>
          <p className="mt-4 text-[#9da7b6] leading-relaxed">
            From sport browse to settlement — one continuous experience designed for clarity and control.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {HOME_FEATURES.map((f) => (
            <FeatureCard key={f.title} icon={f.icon} title={f.title} body={f.body} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/features" className="text-xs font-black uppercase tracking-widest text-[#8cff4a] hover:underline">
            See all features →
          </Link>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#8cff4a] mb-3">
            Built for trust
          </p>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white">
            Clear funds. Clear settlement.
          </h2>
          <p className="mt-4 text-[#9da7b6] leading-relaxed">
            Entries lock when both sides accept. Settlement shows a transparent breakdown —
            including the platform service fee — before anything moves to your wallet.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {SAFETY_POINTS.slice(0, 3).map((p) => (
            <FeatureCard key={p.title} icon={p.icon} title={p.title} body={p.body} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/safety" className="text-xs font-black uppercase tracking-widest text-[#8cff4a] hover:underline">
            Safety & wallet details →
          </Link>
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-20">
        <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white text-center mb-8">
          Common questions
        </h2>
        <div className="space-y-3">
          {FAQS.slice(0, 4).map((item) => (
            <details key={item.q} className="group rounded-2xl bg-white/[0.03] p-5 open:bg-white/[0.05]">
              <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-sm font-black text-white">
                {item.q}
                <span className="text-[#8cff4a] group-open:rotate-45 transition-transform text-lg leading-none">+</span>
              </summary>
              <p className="text-sm text-[#9da7b6] leading-relaxed mt-3">{item.a}</p>
            </details>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/faq" className="text-xs font-black uppercase tracking-widest text-[#8cff4a] hover:underline">
            All FAQs →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24 text-center">
        <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-4">
          Ready for your first lock?
        </h2>
        <p className="text-[#9da7b6] text-base md:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
          Enter as guest to browse upcoming games, or create a free account to start competing with friends.
        </p>
        <CtaRow />
      </section>
    </>
  );
}
